import type { DateRange } from "react-day-picker";
import { useState } from "react";
import { PopoverClose } from "@radix-ui/react-popover";
import { getDateFNSLocale } from "@repo/utils";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useMediaQuery } from "~/hooks/use-media-query";
import { cn } from "~/lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface DateRangePickerProps {
  className?: string;
  initialDateFrom?: Date;
  initialDateTo?: Date;
  onUpdate: (values: { range: DateRange }) => void;
}

export function DateRangePicker({
  className,
  initialDateFrom,
  initialDateTo,
  onUpdate,
}: DateRangePickerProps) {
  const { t, i18n } = useTranslation("common");

  const DATE_PICKER_PRESETS = {
    today: t("DatePicker.Presets.today"),
    yesterday: t("DatePicker.Presets.yesterday"),
    last7days: t("DatePicker.Presets.last7days"),
    thisweek: t("DatePicker.Presets.thisweek"),
    lastweek: t("DatePicker.Presets.lastweek"),
    thismonth: t("DatePicker.Presets.thismonth"),
    lastmonth: t("DatePicker.Presets.lastmonth"),
  } as const;

  const [selectedPreset, setSelectedPreset] = useState<string>();
  const isMobile = useMediaQuery(768);
  const [date, setDate] = useState<DateRange | undefined>({
    from: initialDateFrom ?? new Date(),
    to: initialDateTo ?? new Date(),
  });

  const handleUpdate = () => {
    if (!date?.from || !date.to) return;

    onUpdate({ range: date });
  };

  const handleCancel = () => {
    setDate({
      from: initialDateFrom ?? new Date(),
      to: initialDateTo ?? new Date(),
    });
  };

  const handlePreset = (preset: string) => {
    switch (preset) {
      case DATE_PICKER_PRESETS.today:
        setDate({
          from: new Date(),
          to: new Date(),
        });
        break;
      case DATE_PICKER_PRESETS.yesterday:
        setDate({
          from: addDays(new Date(), -1),
          to: addDays(new Date(), -1),
        });
        break;
      case DATE_PICKER_PRESETS.last7days:
        setDate({
          from: addDays(new Date(), -7),
          to: new Date(),
        });
        break;
      case DATE_PICKER_PRESETS.thisweek:
        setDate({
          from: startOfWeek(new Date(), { weekStartsOn: 1 }),
          to: endOfWeek(new Date()),
        });
        break;
      case DATE_PICKER_PRESETS.lastweek:
        setDate({
          from: startOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
          to: endOfWeek(addDays(new Date(), -7)),
        });
        break;
      case DATE_PICKER_PRESETS.thismonth:
        setDate({
          from: startOfMonth(new Date()),
          to: endOfMonth(new Date()),
        });
        break;
      case DATE_PICKER_PRESETS.lastmonth:
        setDate({
          from: startOfMonth(subMonths(new Date(), 1)),
          to: endOfMonth(subMonths(new Date(), 1)),
        });
        break;
    }
    setSelectedPreset(preset);
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-center text-center text-lg font-normal",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "P")} - {format(date.to, "P")}
                </>
              ) : (
                format(date.from, "P")
              )
            ) : (
              <span>{t("DatePicker.pick_a_date")}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          onInteractOutside={() => {
            handleCancel();
          }}
          style={{ width: isMobile ? "var(--radix-popover-trigger-width)" : "auto" }}
          className="w-auto p-0"
          align="start"
        >
          <div className="flex flex-row max-h-[50dvh] overflow-auto">
            <div className="flex flex-col h-full flex-grow">
              <div className="flex flex-col gap-2">
                <Calendar
                  weekStartsOn={1}
                  locale={getDateFNSLocale(i18n.language)}
                  mode="range"
                  defaultMonth={date?.from}
                  selected={date}
                  onSelect={(range) => {
                    setSelectedPreset(undefined);
                    if (range?.to === undefined) {
                      setDate({
                        from: range?.from,
                        to: range?.from,
                      });
                    } else {
                      setDate(range);
                    }
                  }}
                  numberOfMonths={2}
                />
              </div>
              <div className="flex flex-row gap-2 p-2 sticky bottom-0 bg-popover border-t">
                <PopoverClose>
                  <Button onClick={handleCancel}>
                    {t("DatePicker.cancel")}
                  </Button>
                </PopoverClose>
                <PopoverClose>
                  <Button onClick={handleUpdate} variant="brand">
                    {t("DatePicker.update")}
                  </Button>
                </PopoverClose>
              </div>
            </div>
            {
              <div className="border-l p-2 flex flex-col gap-2 h-full sticky top-0">
                {Object.entries(DATE_PICKER_PRESETS).map(([preset, label]) => (
                  <Button
                    variant={selectedPreset === label ? "success" : "outline"}
                    key={preset}
                    onClick={() => {
                      handlePreset(label);
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            }
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
